if (!customElements.get("product-label-popup")) {
  customElements.define(
    "product-label-popup",
    class ProductLabelPopup extends HTMLElement {
      constructor() {
        super();

        this.handleTriggerClick = this.handleTriggerClick.bind(this);
        this.handleTriggerKeydown = this.handleTriggerKeydown.bind(this);
        this.handleCloseClick = this.handleCloseClick.bind(this);
        this.handleKeydown = this.handleKeydown.bind(this);
      }

      connectedCallback() {
        this.trigger = this.querySelector(".product-label-popup__trigger");
        this.modal = this.querySelector(".product-label-popup__modal");

        if (!this.trigger || !this.modal) return;

        this.closeControls = this.modal.querySelectorAll(
          "[data-product-label-popup-close]",
        );
        this.closeButton = this.modal.querySelector(
          ".product-label-popup__close",
        );

        this.trigger.addEventListener("click", this.handleTriggerClick);
        this.trigger.addEventListener("keydown", this.handleTriggerKeydown);
        this.closeControls.forEach((control) => {
          control.addEventListener("click", this.handleCloseClick);
        });

        // Escape transformed section ancestors and guarantee a viewport-level overlay.
        document.body.appendChild(this.modal);
      }

      disconnectedCallback() {
        this.trigger?.removeEventListener("click", this.handleTriggerClick);
        this.trigger?.removeEventListener("keydown", this.handleTriggerKeydown);
        this.closeControls?.forEach((control) => {
          control.removeEventListener("click", this.handleCloseClick);
        });
        document.removeEventListener("keydown", this.handleKeydown);

        if (this.hasAttribute("open")) {
          this.removeAttribute("open");
          this.unlockPageScroll();
        }

        this.modal?.remove();
      }

      handleTriggerClick(event) {
        event.preventDefault();
        this.openPopup();
      }

      handleTriggerKeydown(event) {
        if (event.key !== "Enter" && event.key !== " ") return;

        event.preventDefault();
        this.openPopup();
      }

      handleCloseClick(event) {
        event.preventDefault();
        this.closePopup();
      }

      handleKeydown(event) {
        if (!this.hasAttribute("open")) return;

        if (event.key === "Escape") {
          event.preventDefault();
          this.closePopup();
          return;
        }

        if (event.key === "Tab") {
          this.trapFocus(event);
        }
      }

      getFocusableElements() {
        const selector = [
          "a[href]",
          "button:not([disabled])",
          "input:not([disabled])",
          "select:not([disabled])",
          "textarea:not([disabled])",
          '[tabindex]:not([tabindex="-1"])',
        ].join(",");

        return Array.from(this.modal.querySelectorAll(selector)).filter(
          (element) => !element.hasAttribute("hidden"),
        );
      }

      trapFocus(event) {
        const focusableElements = this.getFocusableElements();
        const first = focusableElements[0];
        const last = focusableElements[focusableElements.length - 1];

        if (!first || !last) {
          event.preventDefault();
          return;
        }

        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!this.modal.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      }

      openPopup() {
        const openPopup = document.querySelector("product-label-popup[open]");
        if (openPopup && openPopup !== this) {
          openPopup.closePopup(false);
        }

        this.lastFocusedElement = document.activeElement;
        this.setAttribute("open", "");
        this.modal.removeAttribute("inert");
        this.modal.setAttribute("aria-hidden", "false");
        this.modal.classList.add("is-open");
        this.trigger.setAttribute("aria-expanded", "true");
        document.documentElement.classList.add("product-label-popup-open");
        document.body.classList.add("product-label-popup-open");
        document.addEventListener("keydown", this.handleKeydown);

        // Let the trigger's native click/keyboard activation finish before moving focus.
        setTimeout(() => this.closeButton?.focus(), 50);
      }

      closePopup(restoreFocus = true) {
        if (!this.hasAttribute("open")) return;

        this.removeAttribute("open");
        this.modal.classList.remove("is-open");
        this.modal.setAttribute("aria-hidden", "true");
        this.modal.setAttribute("inert", "");
        this.trigger.setAttribute("aria-expanded", "false");
        document.removeEventListener("keydown", this.handleKeydown);
        this.unlockPageScroll();

        if (restoreFocus && this.lastFocusedElement?.isConnected) {
          this.lastFocusedElement.focus();
        }
      }

      unlockPageScroll() {
        if (document.querySelector("product-label-popup[open]")) return;

        document.documentElement.classList.remove("product-label-popup-open");
        document.body.classList.remove("product-label-popup-open");
      }
    },
  );
}
